import { http, setCompany, getCompany, url_ } from './fexeriaHttp';

const rows = (data) => (Array.isArray(data) ? data : data?.results || []);

export function errorMessage(err, fallback = 'No se pudo completar la operación.') {
  if (err?.code === 'ERR_CANCELED') return '';
  const d = err?.response?.data;
  if (typeof d === 'string' && d.length < 300) return d;
  if (d?.detail) return String(d.detail);
  if (d?.message) return String(d.message);
  if (d?.error) return String(d.error);
  if (d && typeof d === 'object') {
    const first = Object.values(d).flat()[0];
    if (typeof first === 'string') return first;
  }
  return err?.message && !err.response ? err.message : fallback;
}

export function journeyErrors(err) {
  const list = err?.response?.data?.errors;
  return Array.isArray(list) ? list.map((e) => ({ path: e.path || '', message: e.message || String(e) })) : null;
}

function real() {
  const url = url_;
  return {
    listDeliverables: (module) => http.get(url('deliverables/'), { params: { module } }).then((r) => rows(r.data)),
    createDeliverable: (payload) => http.post(url('deliverables/'), payload).then((r) => r.data),
    updateDeliverable: (id, body) => http.patch(url(`deliverables/${id}/`), body).then((r) => r.data),
    deleteDeliverable: (id) => http.delete(url(`deliverables/${id}/`)).then(() => null),
    reorder: (module, ids) => http.post(url('deliverables/reorder/'), { module, ids }).then((r) => rows(r.data)),
    setContent: (id, { file, url: link }) => {
      if (file) {
        const form = new FormData();
        form.append('file', file);
        return http.put(url(`deliverables/${id}/content/`), form).then((r) => r.data);
      }
      return http.put(url(`deliverables/${id}/content/`), { url: link }).then((r) => r.data);
    },
    removeContent: (id) => http.delete(url(`deliverables/${id}/content/`)).then((r) => r.data),
    setPublished: (id, visible) => http.post(url(`deliverables/${id}/publish/`), { visible }).then((r) => r.data),
    // Sin URL pública: se baja como blob con el token. En un error el cuerpo llega como Blob y se lee su { detail }.
    download: (id, { inline = false } = {}) =>
      http
        .get(url(`deliverables/${id}/download/`), { responseType: 'blob', params: inline ? { inline: 1 } : {} })
        .then((r) => {
          const cd = r.headers?.['content-disposition'] || '';
          const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
          return { blob: r.data, fileName: m ? decodeURIComponent(m[1]) : `entregable-${id}`, mime: r.data?.type || '' };
        })
        .catch(async (e) => {
          const d = e?.response?.data;
          if (d instanceof Blob) {
            try {
              e.response.data = JSON.parse(await d.text());
            } catch (x) {
              e.response.data = {};
            }
          }
          throw e;
        }),
    getJourney: () => http.get(url('journey/')).then((r) => r.data),
    saveJourney: (journey) => http.put(url('journey/'), journey).then((r) => r.data),
    importJourney: (raw) => http.post(url('journey/import/'), raw).then((r) => r.data),
    exportJourney: () => http.get(url('journey/export/')).then((r) => r.data),
    restoreJourney: () => http.post(url('journey/restore/')).then((r) => r.data),
  };
}

// Del contrato del back (kind, position, has_content, is_published, ...) a la forma que usa el front.
export function normalizeDeliverable(x) {
  const isLink = x.kind === 'link';
  const hasContent = !!x.has_content;
  let content = null;
  if (hasContent) {
    content = isLink
      ? { kind: 'link', url: x.url }
      : {
          kind: 'file',
          file_name: x.file_name || x.name,
          size: x.file_size || 0,
          uploaded_at: x.uploaded_at || null,
          uploaded_by: x.uploaded_by || null,
        };
  }
  return {
    id: x.id,
    module: x.module,
    name: x.name || '',
    description: x.description || '',
    stage: Number(x.stage) || 0,
    order: x.position,
    type: isLink ? 'link' : 'file',
    kind: x.kind || (isLink ? 'link' : 'archivo'),
    publishedBy: x.published_by || null,
    formatLabel: x.format_label || '',
    content,
    visible: !!x.is_published,
    published_at: x.published_at || null,
    hasContent,
    isLink,
  };
}

// Rol y empresa vienen de GET /api/fexeria/access/ (admin SipMóvil sin empresa: company null; 403: sin acceso).
export async function fetchAccess(userInfo) {
  const isSuper = (userInfo?.groups || []).includes('ADMINISTRADOR SIPMOVIL');
  // Solo el admin SipMóvil manda company_id.
  if (!isSuper) setCompany(null);
  try {
    const { data } = await http.get(url_('access/'));
    return {
      denied: false,
      error: '',
      role: data.role,
      canConfigure: !!data.can_configure,
      company: data.company || null,
      userId: data.user?.id ?? null,
    };
  } catch (e) {
    const status = e?.response?.status;
    // Empresa guardada inexistente o inválida: se olvida y se vuelve al listado.
    if (isSuper && getCompany() && (status === 400 || status === 404)) {
      setCompany(null);
      return fetchAccess(userInfo);
    }
    if (status === 403 || status === 401) {
      return { denied: true, error: '', role: null, canConfigure: false, company: null };
    }
    if (e?.code === 'ERR_CANCELED') throw e;
    // Host no válido o inalcanzable: se olvida la empresa.
    if (e?.hostError && isSuper) {
      setCompany(null);
      return { denied: false, error: e.message, role: 'admin_sipmovil', canConfigure: true, company: null };
    }
    return {
      denied: false,
      error: errorMessage(e, 'No se pudo consultar el acceso a Fexería.'),
      role: null,
      canConfigure: false,
      company: null,
    };
  }
}

export const getApi = () => real();
